// client/src/dnd/emitter_tool/components/stages/WallsStage.js
import React, { useEffect, useRef } from "react";
import * as PIXI from "pixi.js";
import { drawCenterGapWall } from "../../hooks/walls.js";

/**
 * Builds/draws walls and publishes their polygons+segments in image/canvas space.
 *
 * Props:
 *   - enabled?: boolean = true
 *   - app?: PIXI.Application   // optional shared app; if omitted we create one
 *   - onWalls?: (payload: { polygons:Array<Array<{x,y}>>, segments:Array<{x1,y1,x2,y2}>, space:"image", dpr:number }) => void
 *
 * Global (legacy):
 *   - window.__ET_WALLS = { polygons, segments, space:"image" }
 *   - dispatches "EmitterTool:WallsUpdated" with same payload
 */
export default function WallsStage({ enabled = true, app: externalApp = null, onWalls = null }) {
  const mountRef = useRef(null);
  const appRef = useRef(null);
  const layerRef = useRef(null);
  const owningApp = useRef(false);

  // local helper (avoid importing polysToSegments)
  function polysToSegments(polys) {
    const segs = [];
    for (const poly of polys || []) {
      for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        segs.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
    }
    return segs;
  }

  useEffect(() => {
    const mount = mountRef.current;

    // 1) Ensure we have an app (use external if provided)
    if (!appRef.current) {
      if (externalApp) {
        appRef.current = externalApp;
        owningApp.current = false;
      } else {
        if (!mount) return;
        const app = new PIXI.Application({
          antialias: false,
          backgroundAlpha: 0,
          resizeTo: mount,
          powerPreference: "high-performance",
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });
        mount.appendChild(app.view);
        appRef.current = app;
        owningApp.current = true;
      }
    }

    const app = appRef.current;

    // 2) Build walls (visuals + polygons), publish payload
    const buildAndPublish = () => {
      // clear prior layer
      if (layerRef.current) {
        try { layerRef.current.parent?.removeChild(layerRef.current); } catch {}
        try { layerRef.current.destroy({ children: true }); } catch {}
        layerRef.current = null;
      }

      // draw walls; the util returns { layer, polys }
      const { layer, polys } = drawCenterGapWall(app);
      layerRef.current = layer;
      app.stage.addChild(layer);
      app.stage.sortChildren?.();

      const segments = polysToSegments(polys);
      const payload = {
        polygons: polys,
        segments,
        space: "image",
        dpr: 1,
      };

      // publish globally for legacy readers
      try {
        window.__ET_WALLS = payload;
        window.dispatchEvent?.(new CustomEvent("EmitterTool:WallsUpdated", { detail: payload }));
      } catch {}

      // notify modern consumers
      if (typeof onWalls === "function") {
        try { onWalls(payload); } catch (e) { console.warn("[WallsStage] onWalls threw:", e); }
      }
    };

    buildAndPublish();

    // 3) Rebuild on resize (keeps geometry aligned to canvas size)
    const onResize = () => buildAndPublish();
    app.renderer.on?.("resize", onResize);

    return () => {
      try { app.renderer.off?.("resize", onResize); } catch {}
      try { delete window.__ET_WALLS; } catch {}

      if (layerRef.current) {
        try { layerRef.current.parent?.removeChild(layerRef.current); } catch {}
        try { layerRef.current.destroy({ children: true }); } catch {}
      }
      layerRef.current = null;

      if (owningApp.current) {
        try { mount?.removeChild(app.view); } catch {}
        app.destroy(true, { children: true, texture: false, baseTexture: false });
        appRef.current = null;
        owningApp.current = false;
      }
    };
  // re-run if we switch to/from an external app instance
  }, [externalApp]);

  // show/hide layer when enabled flips
  useEffect(() => {
    if (layerRef.current) layerRef.current.visible = !!enabled;
  }, [enabled]);

  return (
    <div
      ref={mountRef}
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    />
  );
}
